import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type Nullable } from '@dni-types/utilities/nullable'
import { type CalculateRequestOrderItem } from '@hooks-api/types/apiRequest'
import { type SetStateAction } from 'react'
import * as cartService from '../../core/cartService'
import { type FpB2bCartValues, type CartItem, type PartialCartItem } from '../../types'
import { validateCartItem } from '../../utils/validateCartItem'
import { cartStoreMap, type PlatformType } from './store-map'

export const useFpB2bCartService = (campaignId: number | null, platform: PlatformType) => {
  const { useCartStore, cartDefaultValues } = cartStoreMap[platform]
  const [cartValues, setCartValues] = useCartStore(campaignId)

  const deleteCartItem = useCallbackEvent((item: PartialCartItem) => setCartValues((state) => {
    if (!validateCartItem(item)) return state
    return {
      ...state,
      cart: cartService.deleteCartItem(state.cart, item),
    }
  }))

  const updateCartItemButlerIsDeleted = useCallbackEvent((item: PartialCartItem, isDeleted: boolean) => {
    setCartValues((state) => {
      if (!validateCartItem(item)) return state
      return {
        ...state,
        cart: cartService.updateCartItemButlerIsDeleted(state.cart, item, isDeleted),
      }
    })
  })

  const updateCartItemButlerChecked = useCallbackEvent((item: PartialCartItem, checked: boolean) => {
    setCartValues((state) => {
      if (!validateCartItem(item)) return state
      return {
        ...state,
        cart: cartService.updateCartItemButlerChecked(state.cart, item, checked),
      }
    })
  })

  const updateAllCartItemsButlerChecked = useCallbackEvent((checked: boolean) => {
    setCartValues((state) => {
      return {
        ...state,
        cart: cartService.updateAllCartItemsButlerChecked(state.cart, checked),
      }
    })
  })

  const upsertCartItem = useCallbackEvent((nextItem: PartialCartItem) => {
    const { prevState, nextState } = setCartValues((state) => {
      if (!validateCartItem(nextItem)) return state
      return {
        ...state,
        cart: cartService.upsertCartItem(state.cart, nextItem),
      }
    })
    return {
      prevState: prevState?.cart.items,
      nextState: nextState?.cart.items,
    }
  })

  const updateCartItems = useCallbackEvent((values: SetStateAction<CartItem[]>) => {
    setCartValues((state) => {
      const items = values instanceof Function ? values(state.cart.items) : values
      return {
        ...state,
        cart: cartService.updateCartItems(state.cart, items),
      }
    })
  })

  const replaceCartItem = useCallbackEvent((targetCartItemId: CartItem['cartItemId'], item: PartialCartItem) => {
    setCartValues((state) => {
      if (!validateCartItem(item)) return state
      return {
        ...state,
        cart: cartService.replaceCartItem(state.cart, targetCartItemId, item),
      }
    })
  })

  const resetCartValues = useCallbackEvent((args: { excludes?: (keyof FpB2bCartValues)[] } = {}) => {
    const { excludes } = args
    setCartValues((state) => {
      const newState = { ...cartDefaultValues }
      excludes?.forEach((key) => {
        if (key in newState) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete newState[key]
        }
      })
      return {
        ...state,
        ...newState,
      }
    })
  })

  /** 只換商品（含加購品），優惠券、配送、店家等其他欄位保留；品項經 cartService 正規化（補 butler），不直接塞進 items */
  const overwriteCartItems = useCallbackEvent((items: PartialCartItem[]) => {
    setCartValues(state => ({
      ...state,
      cart: items.filter(validateCartItem).reduce((cart, item) => cartService.upsertCartItem(cart, item), cartDefaultValues.cart),
    }))
  })

  /** STM 與 Merchant 使用同一個 Cart Session */
  const syncCartStoreFromSession = useCallbackEvent((
    args: {
      cartValues: Nullable<FpB2bCartValues>
      orderItems: Nullable<CalculateRequestOrderItem[]>
      callback?: () => void
    },
  ) => {
    const { cartValues, orderItems, callback } = args
    if (!cartService.shouldSyncCartStoreFromSession(cartValues?.cart.items)) {
      callback?.()
      return
    }

    const orderItemSkus = orderItems?.map(item => item.sku)
    setCartValues(state => cartService.syncFpB2bCartStoreFromECommerceSession(state, cartValues, orderItemSkus), callback)
  })

  const isCartEmpty = cartService.getIsCartEmpty(cartValues.cart)
  const cartItemTotalQuantity = cartService.getCartItemTotalQuantity(cartValues.cart)

  return {
    ...cartValues.cart,
    isCartEmpty,
    cartItemTotalQuantity,
    syncCartStoreFromSession,
    resetCartValues,
    deleteCartItem,
    upsertCartItem,
    updateCartItems,
    updateCartItemButlerIsDeleted,
    updateCartItemButlerChecked,
    updateAllCartItemsButlerChecked,
    replaceCartItem,
    overwriteCartItems,
  }
}
